/*
 * etusivu-reitti.js — the vuori/polku scroll band.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THIRD SCOPED EXCEPTION to editable-sections-standard §1 rule 4 ("native HTML
 * first, and no JavaScript"). The standard says an exception "does not travel"
 * and that adding a script means making the argument again rather than citing
 * an earlier one. So here is the argument, for one file.
 *
 * THE ARGUMENT. This band is a drawing that is revealed, painted and then held
 * across four movements, with one short line of text riding each movement. The
 * CSS-only way to do that is `animation-timeline: view()` / `scroll()`, which
 * is what §4.9 mandates for a reveal — and it is genuinely the right tool for a
 * reveal, which is one element crossing one threshold. It is the wrong tool
 * here for a reason that is mechanical rather than aesthetic: the paint effect
 * is a MASK GEOMETRY change (`<rect>` x/y/width/height inside `<mask>`), and
 * SVG presentation ATTRIBUTES are not CSS properties, so no CSS timeline can
 * drive them. Neither can it drive the `transform` on `<g>` elements that the
 * clip-and-rise depends on, on Safari, where SVG geometry attributes are still
 * not animatable from CSS. A scroll-linked drawing whose whole content is
 * geometry cannot be authored in CSS today in every browser family — the same
 * test the blog's TOC and the front page's rails were held to, with the same
 * answer.
 *
 * WHAT IT IS NOT: it is not a reveal, not a parallax, and not decoration bolted
 * onto content. The band has no argument of its own that is lost when the file
 * is blocked — see the fallback below.
 *
 * ⚠️ THE BAND PLAYS ONCE, DOWNWARD, AND IS OTHERWISE A STATIC SECTION
 * (2026-09-21, client). That one sentence is the whole state machine, and
 * `armed` is the one variable that holds it.
 *
 *   ARMED — the reader is coming at the band from above and has not finished
 *   it. The track carries its full scroll length, the stage is pinned, and the
 *   drawing is a function of scroll position exactly as before.
 *
 *   NOT ARMED — the scene has been watched to the end. `syncState()` DROPS THE
 *   TRACK TO ONE SCREEN (`data-reitti-done`, handled in the section's
 *   stylesheet) and the band becomes an ordinary full-bleed section holding the
 *   finished drawing. This is the answer to the second half of the client's
 *   note: with the animation over, eight screens of scroll that change nothing
 *   do not read as a long shot, they read as the page being stuck. There is
 *   nothing left to scrub, so there is no reason to keep the scrubbing length.
 *
 * It re-arms when the band is a full viewport below the fold — far enough that
 * a nudge at the boundary cannot flicker it — so coming back down from the top
 * of the page plays the whole thing again.
 *
 * ⚠️ COLLAPSING A SECTION CHANGES THE SCROLL HEIGHT ABOVE THE READER, so every
 * height change is paired with a compensating `scrollTop` write in the same
 * task, and scroll anchoring is suppressed for that frame or the browser
 * compensates a second time. `setCollapsed()` is the only place this happens
 * and it carries the three cases. The state also rides in `history.state`, for
 * the reason spelled out on `rememberState()`.
 *
 * ⚠️ THE PAINT IS A WIPE, AND THAT IS THE SECOND ANSWER TO THE SAME QUESTION.
 * On 2026-09-21 the mountain was rebuilt to paint ITSELF — twenty-one brush
 * strokes following the drawing's own centreline, drawn in order, each holding
 * where the line crosses itself — and the client looked at it and chose the
 * wipe back: *"more like a wall of paint from left to right"*. So both masks
 * are rects again and `render()` is four lines shorter.
 *
 * ⚠️ THE OTHER ONE IS NOT LOST AND SHOULD NOT BE REBUILT FROM SCRATCH.
 * material/svg-animations/centerline/ holds the generator, the traced
 * centreline and a README: it recovers a drawing's route out of artwork that
 * is a filled OUTLINE with no stroke anywhere to travel along, which is the
 * hard half of the problem and is done. If this ever comes back — or when
 * Susanna's real brush scribbles arrive — start there.
 *
 * ⚠️ AND THE SCENE NOW ENDS AT THE END OF THE TRACK. It used to finish at 0.78
 * and spend the last fifth receding; the recede is gone, so that fifth was dead
 * scroll and the client named it. Every `P` value was divided by 0.78 and the
 * TRACK WAS SHORTENED BY THE SAME PROPORTION in the stylesheet, which leaves
 * the pace the client tuned on 2026-09-16 untouched to the pixel and removes
 * only the emptiness. Retuning one without the other changes the pace.
 *
 * ⚠️ WITH THIS FILE BLOCKED, OR UNDER `prefers-reduced-motion: reduce`, THE
 * BAND IS COMPLETE. The section ships the finished drawing and every text beat
 * in ordinary flow markup, as a numbered list under the picture. The track has
 * no extra height and the stage does not stick (both live inside
 * `@media (prefers-reduced-motion: no-preference)` in the section's stylesheet,
 * so no-motion is the DEFAULT state rather than a fallback). Nothing on this
 * page is said only inside the animation. That is the content-parity rule and
 * it is binding: a beat may only ever echo copy that exists in full elsewhere.
 *
 * CONSTRAINTS, inherited from the two earlier exceptions and all binding:
 *   - it does nothing if its elements are absent;
 *   - everything reads correctly with the script blocked;
 *   - no library, no polyfill, no third-party request;
 *   - motion is opt-in, checked live rather than once at load, because the OS
 *     setting can change mid-session;
 *   - NO customer-visible string is created here. Every label, including every
 *     `aria-label` and all three states of the control button, is rendered by
 *     the section from a schema setting, because a string born in JavaScript is
 *     a string Susanna cannot edit (editable-sections-standard §3);
 *   - CLAUDE.md JS conventions: `globalThis` over `window`, `Number.*` over the
 *     globals, `element.remove()`.
 *
 * ⚠️ THE 990px SCROLL-CONTAINER TRAP DOES APPLY HERE, unlike in
 * etusivu-kaista.js. Above 990px Horizon puts `overflow: hidden` on html/body
 * and scrolls `.page-wrapper` instead, so `globalThis.scrollY` is permanently 0
 * and a `scroll` listener on `window` NEVER FIRES on desktop. Every scroll read
 * below goes through @theme/scroll-container. `getBoundingClientRect()` is
 * viewport-relative and correct in both layouts, so it is what the progress
 * maths is built on.
 */

import { getIntersectionRoot } from '@theme/scroll-container';

const doc = globalThis.document;
const REDUCED = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------------------
 * The choreography. Ported from material/svg-animations/vuori-reveal-demo.html
 * with that demo's tuning panel removed and its shipped defaults baked in, and
 * then with the demo's FOURTH MOVEMENT DROPPED (2026-09-21). The demo receded
 * the finished drawing — scale to 0.3, fade to nothing, optionally a 2px blur
 * and an outBack overshoot — and it was wrong here for a reason that is about
 * the page rather than the effect: the reader spends eight screens watching a
 * picture be drawn and is then shown it being destroyed, which reads as the
 * band failing rather than ending. Nothing tweens the art's scale, opacity or
 * filter any more, so those three lines and the inBack/outBack easings are gone
 * rather than switched off; the demo file still has them if it ever returns.
 *
 * ⚠️ EVERY t VALUE BELOW IS THE DEMO'S DIVIDED BY 0.78, so the scene now fills
 * the track instead of finishing at 0.78 of it. Read them against the shortened
 * `--reitti-track` in the stylesheet: the two were changed together and only
 * both together preserve the pace. In pixels of scroll, nothing here moved.
 * ------------------------------------------------------------------------- */
const P = {
  /* ⚠️ 340 until 2026-09-21. The road is CLIPPED at the horizon and it arrives
     from above it, so every unit of travel is a unit of road hidden behind the
     clip — at 340 the band could be pinned, filling the screen, with two short
     tips of path showing and nothing else. Shortening the arrival is what
     makes the road *there* when the reader gets there. */
  rTravel: 200, // the path arrives from this far above, in SVG user units
  mTravel: 300, // the mountain rises from this far below
  hDrop: 220, // how far the horizon settles as the mountain lands
  tRoadEnd: 0.6977, // 1 — path in place
  /* ⚠️ BOTH MOVED +0.15 (three seconds) ON 2026-09-22 — client: *"make the
     mountain appear like 3 seconds later than it does now … the path would be
     further finished before the mountain actually fades in / rises up."*

     ⚠️ THE PAIR MOVES TOGETHER, and moving only the start would have been the
     obvious mistake. `tMtnEnd` is where the rise finishes, so leaving it at
     0.403 would have kept the landing time fixed and squeezed the rise from
     5.83 s into 2.83 s — the mountain would arrive later AND twice as fast,
     which is not what was asked and is the opposite of the pace the run has
     been tuned for all day. Shifted together, the rise keeps its length and
     only its position on the timeline changes.

     What the delay buys, in the client's words, is road: the path now paints
     HALF its climb before the mountain is visible at all, against 19 % before
     — measured off these numbers rather than estimated. What it costs is the gap before the paint — the mountain lands at
     11.06 s and its own paint starts at 12.64 s, so the pause between the two
     is 1.58 s where it used to be 4.58 s. That is the number to watch if this
     moves again; below about a second the landing and the first brush stroke
     start to read as one event. */
  tMtnStart: 0.2614, // 2 — mountain starts rising
  tMtnEnd: 0.553, // 3 — mountain in place
  /* 4 — mountain paint starts.
     ⚠️ THIS IS NO LONGER `VAIHEET.maalaus[0]`, AND THE GAP IS DELIBERATE — the
     lock that used to live on this line was broken on purpose on 2026-09-22
     and the reason has to survive, because the two values now sit 1 s apart
     and that looks exactly like the drift the lock existed to prevent.

     The lock: on 2026-09-21 the client asked for the mountain's paint to start
     on the frame its line appears — *"paint out the mountain when 'Minä pidän
     suunnan näkyvissä.' comes visible"* — so `tMtnPaint` and `maalaus[0]` were
     made one event rather than two that happen to be close.

     What broke it: on 2026-09-22 the client moved screen time from *"Sinä
     valitset määränpään..."* to the line after it, *"ja minä pidän suunnan
     kurssissa"*, on the grounds that the longer line deserves longer — a
     second, then on seeing it a second more, so TWO in total. That is a request about TEXT PACING, and honouring it by
     moving both together would have silently retimed three things nobody asked
     about: the mountain's paint would gain a second, the horizon's sweep is
     pinned to this value so it would end a second earlier, and the road's
     paint window would lose that second.

     So the beat moved and the paint did not. The line now appears at 10.64 s
     and the first brush stroke lands at 12.64 s — TWO seconds of the new line
     on screen before any paint, and for the first 0.42 s of that the mountain
     is still finishing its rise (it lands at 11.06 s). The line now introduces
     the mountain instead of arriving with its paint, which is a different
     reading of the band and the client's, not a drift.

     ⚠️ THE GAP IS THE NUMBER TO WATCH IF THIS MOVES AGAIN. Every second taken
     off `vuori` widens it, and past roughly three the line stops reading as an
     introduction and starts reading as a line waiting for something to
     happen.

     ⚠️ IF THE CLIENT WANTS THE OLD COINCIDENCE BACK, the fix is to set this to
     `maalaus[0]` again (0.532) — and then check `tRoadPaintEnd`, which is
     pinned to this value and has to move with it. */
  tMtnPaint: 0.632,
  tDone: 1, // 5 — the scene is finished, on the last frame of the run
  /* ⚠️ THE ROAD'S PAINT WINDOW, written out rather than derived. It used to be
     `(p - 0.026) / (tRoadEnd + 0.051 - 0.026)` — two hand-tuned offsets hung
     off a fourth number, which meant retuning the timeline silently retuned
     the paint by a different proportion. These are the same two instants the
     old expression produced, carried through the 2026-09-21 restretch. */
  tRoadPaintStart: 0.0228,
  /* ⚠️ THIS IS `tMtnPaint`, AND THAT IS THE POINT — client, 2026-09-22: *"the
     horizon paint that goes from right to left should finish the same second
     on its left edge when the painting out of the mountain begins"*. It used
     to be 0.8285, which ran the horizon's sweep 1.93 s PAST the first stroke
     of the mountain, so two wipes were crossing the picture in opposite
     directions at once and the hand-over the sweep exists to create never
     happened. The two are one event now, like `tMtnPaint` and `maalaus[0]`
     already are: move one, move the other. */
  tRoadPaintEnd: 0.632,
  /* Where the road's paint stops climbing and starts sweeping sideways. 0.78
     leaves the last fifth of the road's phase for the horizon: enough to watch
     it draw, short enough that it still reads as the tail of the road's own
     movement rather than a third act. */
  horAt: 0.78,
};

/** ⚠️ WHERE THE CLIMB HANDS OVER TO THE SIDEWAYS SWEEP, in SVG user units,
 *  and it is MEASURED off the artwork rather than chosen — 2026-09-21, client:
 *  *"the road's painting is finished to the very end of the horizon, now it
 *  ends before it hits the horizon"*. They were right and it was 32 units of
 *  winding road.
 *
 *  Sampling the linework with `isPointInFill` across the rows below the road
 *  clip (y >= 344) gives the profile: the HORIZON's own ink occupies y 336…351
 *  — a wide band, 46…1286 across — and from y 352 down there is nothing but
 *  the two strands of the path, never more than ~70 units wide. The hand-over
 *  belongs at the bottom edge of the horizon's ink, and it was at 384.
 *
 *  So the climb stopped 32 units short and those 32 units of road were left to
 *  the horizontal sweep, which paints them last and from the side. That is the
 *  gap the client saw: the paint climbing the path, stopping below the
 *  horizon, and the rest arriving sideways a beat later.
 *
 *  ⚠️ DO NOT CLOSE IT COMPLETELY. The hand-over exists at all because the
 *  horizon is a HORIZONTAL line and a horizontal edge climbing into it meets
 *  all eleven hundred units of it in one frame — the flash the client caught
 *  earlier the same day. 352 is as high as the climb can go without touching
 *  the horizon's ink, which is exactly where it should stop. */
const ROAD_BASE = 674; /* the bottom of the artwork, where the climb starts */
const ROAD_TOP = 352; /* the horizon ink's bottom edge; see above */
const ROAD_CLIMB = ROAD_BASE - ROAD_TOP; /* 322, was 290 */

const H0 = 344; /* the resting horizon, in SVG user units */

/* Which slice of the scroll each named movement owns. A beat block picks its
   movement by name in the schema, so retuning the animation never desynchronises
   the copy — this table is the single place the two are tied together. */
/* ⚠️ THE FIRST THREE WINDOWS ARE THE OLD ONES, to the pixel — 0.34 and 0.56
   over a scene that stopped at 0.78 are 0.436 and 0.718 over one that fills the
   track. The fourth is where the change shows: `loitontuminen` no longer has a
   movement of its own, so it now rides the FINAL STRETCH OF THE MOUNTAIN'S
   PAINT, which is the last thing that happens. Its key keeps the old spelling
   because it is a stored block setting in templates/index.json — renaming it
   would silently unbind every fourth beat on the live page. The schema's label
   was rewritten instead. */
/* ⚠️ RESTRETCHED 2026-09-21 (client), and the stretch is NOT uniform. The
   brief was precise: *"from the very start up until 'Minä pidän suunnan
   näkyvissä.' it should be much slower … 4 more seconds in total, so 2 for
   each step/text"*. So the first two beats get two seconds each and the last
   two keep the pace they had. At PLAY_MS = 16000:

     polku          2.00 →  7.28 s   (was 0.00 →  5.28)   +2 s
     vuori          7.28 → 12.64 s   (was 5.28 →  8.64)   +2 s
     maalaus       12.64 → 14.32 s   (was 8.64 → 10.32)   unchanged, 1.68 s
     loitontuminen 14.32 → 16.00 s   (was 10.32 → 12.00)  unchanged, 1.68 s

   Every `P` value above was carried across by the same piecewise map — ×1.0972
   before the old 0.72, and 0.79 + (t − 0.72) × 0.75 after it — so the movements
   still land where they landed relative to the words. Retuning one without the
   other desynchronises the copy, which is the whole reason this table exists.

   ⚠️ RESTRETCHED AGAIN 2026-09-21, same day, same reading of the brief:
   *"make the painting of the mountain 2s slower in total, and add 1s for each
   title length"*. At PLAY_MS = 20000:

     polku          2.00 →  8.28 s   (was 2.00 →  7.28)   +1 s
     vuori          8.28 → 14.64 s   (was 7.28 → 12.64)   +1 s
     maalaus       14.64 → 17.32 s   (was 12.64 → 14.32)  +1 s
     loitontuminen 17.32 → 20.00 s   (was 14.32 → 16.00)  +1 s

   ⚠️ THE TWO HALVES OF THAT BRIEF WERE ONE CHANGE, and the arithmetic below
   held while `tMtnPaint` and `maalaus[0]` were the same number: the paint ran
   from `tMtnPaint` to `tDone`, so the paint window WAS exactly the last two
   beats, and giving those two a second each took the paint from 3.36 s to
   5.36 s — the "2 s slower" to the millisecond.

   ⚠️ THAT EQUIVALENCE NO LONGER HOLDS. It was broken deliberately on
   2026-09-22, when a second of screen time moved from `vuori` to `maalaus`
   without the paint following it; see the note at `tMtnPaint`. The paint
   window is now 0.632 → 1 (7.36 s) while `maalaus` + `loitontuminen` is
   0.532 → 1 (9.36 s), so **the paint is two seconds SHORTER than the words
   riding it** and the last two beats can no longer be used to derive its
   length. Read `tMtnPaint` and `tDone` directly instead — the reasoning below
   is kept as the record of how the numbers got here, not as a live formula.

   The `P` values were carried across by the piecewise map the same way as
   last time — the lead-in unchanged, then ×1.1894 through `polku`, ×1.1866
   through `vuori`, ×1.5952 through the paint — so every movement still lands
   on the same word it landed on before. Spot-checked in absolute seconds:
   the road's paint still starts at 0.456 s, the mountain still starts rising
   at 2.33 s and is in place at 10.06 s, and the paint still begins on the
   frame `maalaus` does.

   ⚠️ RESTRETCHED A THIRD TIME 2026-09-22 (client), and this one is a SWAP
   rather than a stretch — the run is still 20 s. The brief: *"slow down the
   painting of the mountain by 2s … quicken the painting out of the road and
   the whole road animation step by 2s"*. Two seconds move from the front of
   the run to the back, so PLAY_MS does not change:

     polku          2.00 →  6.28 s   (was 2.00 →  8.28)   −2 s
     vuori          6.28 → 12.64 s   (was 8.28 → 14.64)   unchanged, 6.36 s
     maalaus       12.64 → 16.32 s   (was 14.64 → 17.32)  +1 s
     loitontuminen 16.32 → 20.00 s   (was 17.32 → 20.00)  +1 s

   `vuori` KEEPS ITS LENGTH ON PURPOSE. Only the road was asked to hurry, so
   the two seconds come out of `polku` alone and everything after it simply
   starts two seconds earlier. And the +1/+1 on the last two beats is again
   ONE change with the mountain's paint, not two: the paint IS those two beats
   (see `tMtnPaint`), so giving them a second each is the "2 s slower" exactly
   — 5.36 s becomes 7.36 s.

   `P` was carried across by the same piecewise map as before, in three
   segments: identity to 0.1, ×0.6815 through `polku`, a flat −0.1 shift
   through `vuori` (same length, earlier), ×1.3731 through the paint. So the
   road's paint still starts at 0.456 s — the wordless opening is untouched —
   the mountain starts rising at 2.23 s and is in place at 8.06 s.

   ⚠️ `tRoadPaintEnd` IS THE ONE VALUE THE MAP DOES NOT PRODUCE. It is pinned
   to `tMtnPaint` by the first half of the same brief, which is a stronger
   constraint than carrying the old proportion across; see its note in `P`.
   Between them the road's paint window loses 3.93 s rather than 2 — 2 s from
   the step being quicker, 1.93 s from the horizon no longer running on into
   the mountain's wipe. `horAt` stays at 0.78, so the climb and the sideways
   sweep shorten in the proportion the client tuned: the climb now finishes at
   9.96 s and the horizon closes back to the left over the 2.68 s after it.

   ⚠️ `polku` STARTED AT 0.1 UNTIL 2026-09-22 AND NOW STARTS AT 0, which
   RETIRES the silent opening — and the note it retires is worth keeping rather
   than deleting, because it was a client decision too. On 2026-09-21 the ask
   was *"the first title would fade in after the path has already been painting
   itself out for 2 seconds"*, so the band opened on a road drawing itself in
   silence. On 2026-09-22 the same client asked for the opposite, in as many
   words: *"show the 'Prosessi.' text / fade it in right away when the animation
   starts, so it is visible longer."* The second reading wins; the first is
   recorded here so nobody re-derives it from the gap and puts it back.

   ⚠️ IT IS NOT A PACING CHANGE TO ANYTHING ELSE. Only `polku`'s lower bound
   moved, so the two seconds went to its OWN lines and nothing downstream
   shifted: `polku` still ends at 6.28 s and `vuori` still begins there. Its two
   lines now read for 3.14 s each instead of 2.14 s.

   ⚠️ And the road still starts painting at 0.456 s, NOT at 0 — `tRoadPaintStart`
   was not touched. So the first line now arrives a beat BEFORE the first stroke
   of the road rather than two seconds after it. That is the visible consequence
   of this change and the thing to look at first if it feels wrong.

   The split rule itself changed on the same day, and it is in `build()` rather
   than here: a movement's window is divided evenly between the lines that name
   it, EXCEPT that the final movement's last line is the resting state and
   starts where the scene ends. See the comment at `const windows`. Either way
   Susanna can add or remove a line in the editor and the timing redistributes
   itself with no code change. */
const VAIHEET = {
  polku: [0, 0.314],
  vuori: [0.314, 0.532],
  maalaus: [0.532, 0.816],
  loitontuminen: [0.816, 1.0],
};

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const outCubic = (x) => 1 - (1 - x) ** 3;
/* Slow to start, strongest at the end — the mirror of outCubic, and the client's
   own description of how the mountain should arrive. Quadratic rather than
   cubic on purpose: x³ leaves the fade at 4% one second in and the shape is
   still moving fast behind it, so the mountain reads as popping in late. x² is
   11% at one second and 25% at halfway, which is unmistakably a slow start
   without ever looking like a dropped frame. */
const inQuad = (x) => x * x;

/** The last frame of the scene. */
const PLAY_END = P.tDone;

/* THE WHOLE LENGTH OF THE BAND, and the client's number: twenty seconds from
   an empty stage to the painted mountain, once, when the reader arrives.

   History, because the number has moved a long way and the reasons contradict
   each other on purpose. 11s, then 18s on 2026-09-16 when the client said the
   sequence went by too fast — but that was while the scene was scrubbed along
   a six-screen track, where "too fast" meant the reader's own scrolling ran
   the film faster than they could read. Then 7s when the track was traded for
   a timer, and 12s the same day: seven read as hurried once nobody was
   scrolling it themselves.

   Twelve gave the four beats about three seconds each. Sixteen, on the same
   client's next look at it, gave the first two a great deal more than that
   and left the last two alone. TWENTY, on the look after that, adds a second
   to every beat — so the run opens with two wordless seconds of road being
   drawn, holds its first two lines for ~6.3 s each, and gives the mountain's
   paint 5.36 s instead of 3.36. The arithmetic and the quotes are at
   `VAIHEET`, which is where the beats are.

   It is still the whole band, and a reader who does not care scrolls on —
   nothing holds them here, which is the one thing that lets the number grow.

   ⚠️ If this moves again, move `VAIHEET` and `P` with it. Every fraction in
   both is a number of SECONDS divided by this one. */
const PLAY_MS = 20000;

/** ⚠️ THREE SECONDS, IN MILLISECONDS, AND DERIVED RATHER THAN WRITTEN AS A
 *  FRACTION — client, 2026-09-22: *"within 3s time it would fade in with full
 *  opacity."* Every other number in `P` is a fraction of the run, so a fraction
 *  here would silently become 2.4s or 4s the next time PLAY_MS moves, and the
 *  one thing the client specified about this fade is its length in seconds.
 *  Dividing by PLAY_MS at the point of use keeps it three seconds forever.
 *
 *  It is deliberately NOT tied to the mountain's rise. The rise runs
 *  `tMtnStart → tMtnEnd`, which is 5.83s, and the fade is shorter: the shape
 *  arrives while it is still materialising, which is the whole effect. */
const MTN_FADE_MS = 3000;
const MTN_FADE = MTN_FADE_MS / PLAY_MS;

const mounted = new WeakMap();

function mount(scope) {
  const roots = scope.querySelectorAll?.('[data-reitti]') ?? [];
  for (const root of roots) {
    if (mounted.has(root)) continue;
    const instance = build(root);
    if (instance) mounted.set(root, instance);
  }
}

function build(root) {
  const track = root.querySelector('[data-reitti-track]');
  const stage = root.querySelector('[data-reitti-stage]');
  const art = root.querySelector('[data-reitti-art]');
  const road = root.querySelector('[data-reitti-road]');
  const mtn = root.querySelector('[data-reitti-mtn]');
  const rectM = root.querySelector('[data-reitti-rect-mtn]');
  const wipeR = root.querySelector('[data-reitti-wipe-road]');
  const wipeH = root.querySelector('[data-reitti-wipe-hor]');
  const wipeM = root.querySelector('[data-reitti-wipe-mtn]');

  /** ⚠️ WHERE THE MOUNTAIN'S WIPE STARTS AND STOPS, measured off the artwork
   *  rather than assumed — 2026-09-21, client: *"the painting of the mountain
   *  should end right at the same second when the scroll continues to the next
   *  section"*. It did not, and the reason was arithmetic rather than timing:
   *  the wipe rect ran from x=-400 to x=1800, a 2200-wide sweep, while the ink
   *  only occupies x ≈ 45…1287. So the first FIFTH of the paint phase moved
   *  the edge through empty space before it touched anything, and the last
   *  QUARTER moved it through empty space after the final stroke — the
   *  mountain finished at 0.93 of the track and the band then stayed pinned
   *  for another ~300px with nothing happening.
   *
   *  Measured, not hardcoded, so re-cropping or redrawing the artwork cannot
   *  silently put the dead space back. `getBBox()` is in the same user space
   *  as the mask (both are read in the group's own coordinates, so the rise
   *  transform does not enter into it), and the old numbers are the fallback
   *  for the case where it cannot be measured at all. */
  const WIPE_PAD = 6; /* enough to cover the ink's own width at either end */
  const wipeX = Number.parseFloat(wipeM.getAttribute('x')) || -400;
  /* ⚠️ THE HORIZON'S RECT IS ANCHORED AT ITS RIGHT EDGE, not its left, because
     it paints right to left (client, 2026-09-21) — so what has to be captured
     here is the far side. Read once, at setup, from the markup's own
     attributes: `render()` rewrites this rect's `x` AND `width` on every
     frame, so reading either of them later gets a value the last frame wrote.
     The road and mountain rects keep growing from `wipeX` and need no
     equivalent. */
  const wipeHRight =
    (Number.parseFloat(wipeH.getAttribute('x')) || -400) +
    (Number.parseFloat(wipeH.getAttribute('width')) || 2200);
  let wipeFrom = -400;
  let wipeTo = 1800;
  try {
    const box = mtn.getBBox();
    if (box.width > 0) {
      wipeFrom = box.x - WIPE_PAD;
      wipeTo = box.x + box.width + WIPE_PAD;
    }
  } catch (_) {
    /* getBBox throws on an unrendered SVG in some browsers; the 2200-wide
       sweep still paints everything, it just wastes both ends doing it. */
  }
  const btn = root.querySelector('[data-reitti-btn]');
  const beats = [...root.querySelectorAll('[data-vaihe]')];

  /* Rule 1 of every exception on this front page: do nothing if the elements
     are absent. A partially-edited section must not throw. */
  if (!track || !stage || !art || !road || !mtn || !rectM || !wipeR || !wipeH || !wipeM) return null;

  /* Give every beat its own window. Beats that share a movement split that
     movement evenly between them, in source order, so Susanna can add or remove
     a line in the editor and the timing redistributes itself with no code
     change and no per-beat number to keep in sync. */
  const byVaihe = new Map();
  for (const el of beats) {
    const key = el.dataset.vaihe;
    if (!byVaihe.has(key)) byVaihe.set(key, []);
    byVaihe.get(key).push(el);
  }
  const windows = new Map();
  for (const [key, group] of byVaihe) {
    const [from, to] = VAIHEET[key] ?? VAIHEET.polku;

    /* ⚠️ THE LAST LINE OF THE WHOLE RUN IS THE RESTING STATE, not a beat with a
       slice of its own — client, 2026-09-22: *"it can come visible right at the
       moment when the mountain painting finishes, because it will be visible
       there after the animation has finished, so the viewer has time to read
       it. This way we give more time for the 'Askelmerkit' text."*

       So in the FINAL movement, and only when it carries more than one line,
       the last line starts exactly where the scene ends and the lines before it
       divide the whole movement between them. Concretely, with two lines on
       `loitontuminen` [0.816, 1]: "Askelmerkit..." holds the full 3.68 s of the
       movement instead of half of it, and "ne löydämme yhdessä." arrives on the
       frame the mountain's last stroke lands and then simply stays — read at
       the reader's pace, not the timeline's.

       ⚠️ The `group.length > 1` guard is the whole safety of it. A final
       movement with ONE line must keep the ordinary window: hand it the
       resting-state rule and its only line would appear on the last frame and
       the movement would run silent to that point. */
    const resting = to >= 1 && group.length > 1;
    const jaettavia = resting ? group.length - 1 : group.length;
    const slice = (to - from) / jaettavia;

    group.forEach((el, i) => {
      /* ⚠️ The very last window is CLOSED at the top, where every other one is
         half-open. `p` is clamped to 1 and the test below is `p < w[1]`, so a
         [x, 1) window switches its beat off on the final pixel of the track.
         That used to be invisible — the recede had dissolved the picture by
         then — and since 2026-09-21 the finished drawing is still sitting
         there, so the last line would blink out from under it. */
      const last = i === group.length - 1 && to >= 1;
      if (resting && last) {
        windows.set(el, [to, Infinity]);
        return;
      }
      windows.set(el, [from + i * slice, last ? Infinity : from + (i + 1) * slice]);
    });
  }

  let playing = false;
  let playStart = 0;
  /* Armed = the scene will run when the band arrives. It disarms as the run
     ends and re-arms only when the band has left the screen completely, so a
     reader scrolling back and forth across the boundary cannot retrigger it. */
  let armed = true;

  function paintBeats(p) {
    for (const el of beats) {
      const w = windows.get(el);
      const on = w ? p >= w[0] && p < w[1] : false;
      /* `visibility` rather than `opacity` alone: an opacity-0 beat stays in the
         accessibility tree, so a screenreader browsing the band mid-scroll would
         read every beat at once as a run of disconnected 2–5 word fragments.
         `visibility: hidden` removes it from the tree and from the tab order,
         and it is still animatable, so the crossfade survives. The CSS owns the
         transition; this only flips the flag. */
      el.toggleAttribute('data-active', on);
    }
  }

  function render(p) {
    p = clamp(p);

    /* 1 — the path drops in from the horizon and settles. The opacity runs
       ahead of the movement (×2.6) so the road is THERE, not a ghost, by the
       time the band has taken the screen. ⚠️ Every unit of `rTravel` is a unit
       of road hidden behind the horizon clip, which is why that number, and
       not a head start on the timeline, is what fixed the lag at the opening
       — see `fromScroll()`. */
    const er = outCubic(clamp(p / P.tRoadEnd));
    road.setAttribute('transform', `translate(0 ${(-P.rTravel * (1 - er)).toFixed(1)})`);
    road.style.opacity = clamp(er * 2.6).toFixed(3);

    /* 2 — the mountain rises from behind it. */
    const em = outCubic(clamp((p - P.tMtnStart) / Math.max(0.001, P.tMtnEnd - P.tMtnStart)));
    mtn.setAttribute('transform', `translate(0 ${(P.mTravel * (1 - em)).toFixed(1)})`);
    /* ⚠️ FROM ZERO, OVER THREE SECONDS, ON ITS OWN CLOCK — client, 2026-09-22.
       This RETIRES the atmospheric-depth reading that stood here: the opacity
       used to be `0.35 + em * 0.65`, so the mountain appeared already a third
       visible and brightened as it rose, as if emerging from haze. The client
       asked for it to start at nothing and arrive over three seconds instead,
       and the two cannot both be true — a 0.35 floor is not a fade-in.

       Note what is NOT shared with the line above: `em` is `outCubic` over the
       RISE window and drives the transform; this is `inQuad` over its own
       three-second window and drives only the opacity. Fast-in-slow-out
       movement under slow-in-fast-out opacity is what makes the mountain settle
       into place and then materialise, rather than doing both at once. */
    mtn.style.opacity = inQuad(clamp((p - P.tMtnStart) / MTN_FADE)).toFixed(3);

    /* The horizon starts high and settles as the mountain rises. Only the
       MOUNTAIN clip follows it — a road clip that followed a rising horizon
       would grow upward and start drawing the mountain inside the road layer. */
    const hy = H0 - P.hDrop * (1 - em);
    rectM.setAttribute('height', (hy + 400).toFixed(1));

    /* 3 — paint. The path paints alongside its own arrival; the mountain
       waits until the path has finished and it has landed, and then takes it
       left to right — a wall of paint, not a line being drawn.

       (It was drawn line by line for half a day on 2026-09-21; the client
       chose the wall, and material/svg-animations/centerline/ still holds the
       other one.)

       ⚠️ THE ROAD PAINTS IN TWO MOVEMENTS, and the split is the fix for a
       flash the client caught: "when the horizon gets paint after the road is
       filled, it paints it out in a flash". Their diagnosis was right and it
       is geometry, not timing. The road's wipe is a HORIZONTAL edge climbing
       upward and the horizon is a HORIZONTAL line, so the edge meets the
       whole of it in one frame — eleven hundred units of line arriving at
       once, however slowly the edge moves.

       So the sweep hands over near the top: up to `horAt` the edge climbs the
       winding path, which works because the path is vertical; after it, the
       band the horizon lives in is swept sideways. The two rects are a union
       inside one mask, so they simply add up.

       ⚠️ AND THE SIDEWAYS SWEEP GOES RIGHT TO LEFT, against the mountain
       above it. Client, 2026-09-21: *"the horizon should actually paint from
       right to left, and then the mountain would begin painting just as it
       does now"*. It used to run left to right like everything else, which
       made the horizon read as the first half of the mountain's own wipe —
       one long stroke crossing the picture twice. Opposed, the horizon closes
       back toward the road it came from and the mountain's wipe then starts
       clean from the left as its own gesture.

       Mechanically that means this rect is pinned at its RIGHT edge and grows
       leftward, so both `x` and `width` move — see `wipeHRight`. The mountain
       and the road are unchanged: pinned left, width only. */
    const paintRoad = clamp(
      (p - P.tRoadPaintStart) / Math.max(0.001, P.tRoadPaintEnd - P.tRoadPaintStart)
    );
    const climb = clamp(paintRoad / P.horAt);
    const across = clamp((paintRoad - P.horAt) / Math.max(0.001, 1 - P.horAt));
    const paintMtn = clamp((p - P.tMtnPaint) / Math.max(0.001, P.tDone - P.tMtnPaint));
    /* The mountain's paint IS the end of the scene: it lands on the track's
       last pixel, with the fourth beat riding the final stretch of it. */
    /* The climb now runs all the way to the horizon's ink — see ROAD_TOP. */
    wipeR.setAttribute('y', (ROAD_BASE - climb * ROAD_CLIMB).toFixed(1));
    wipeR.setAttribute('height', (climb * ROAD_CLIMB).toFixed(1));
    const horEdge = wipeTo - (wipeTo - wipeFrom) * across;
    wipeH.setAttribute('x', horEdge.toFixed(1));
    wipeH.setAttribute('width', (wipeHRight - horEdge).toFixed(1));
    /* The edge travels the ink's own width, so the last stroke lands ON the
       last frame of the scene — which is the frame the band unpins and the
       page carries on to `kokemukset`. */
    wipeM.setAttribute('width', (wipeFrom + (wipeTo - wipeFrom) * paintMtn - wipeX).toFixed(1));

    /* 4 — there is no 4, and there is no track left over either. Nothing here
       writes `art.style`, so the figure keeps the scale, opacity and filter the
       stylesheet gives it, all three untouched, for good. */

    paintBeats(p);
  }

  /* --- the run -----------------------------------------------------------
     A twenty-second timeline, started when the band settles into view
     (client, 2026-09-21; see PLAY_MS for how the number got there).
     Everything that used to read the scroll position is gone:
     `fromScroll`, the lead-in, the collapse and its scroll compensation, the
     history-state persistence and the 990px scroll-container dance. They
     existed to scrub a scene along a six-screen track, and there is no track.

     ⚠️ THE PAGE IS NEVER HELD. The reader can scroll away mid-run and the
     band simply leaves; the timeline finishes off-screen and costs nothing.
     Holding the scroll for the length of the run was the other way to read the
     brief and it was explicitly declined — it is scroll-jacking, and a page
     that ignores the wheel reads as broken rather than as deliberate. */

  /** ⚠️ `data-reitti-idle` IS NOW A MODE, NOT A DISAPPEARANCE — client,
   *  2026-09-22: *"ensure the 'Ohita animaatio' button stays intact even after
   *  the animation has finished, but change it instead to have a play icon …
   *  and make it say 'Toista animaatio' … No need to hide the button at any
   *  point."* It used to flip `visibility`, which met the older no-layout-jump
   *  rule by reserving the box and emptying it; the box is now simply always
   *  full, which meets that rule the same way and leaves a working control on
   *  screen instead of a gap.
   *
   *  So this attribute still means exactly "nothing is running" — the
   *  stylesheet swaps which icon and which label are shown, and `onButton`
   *  reads it as which of the two jobs to do. One flag, one truth, no second
   *  state to keep in sync. `hidden` stays the no-JS / reduced-motion state,
   *  where the box should not exist at all. */
  function quiet(on) {
    if (!btn) return;
    btn.toggleAttribute('data-reitti-idle', on);
  }

  function finish() {
    playing = false;
    armed = false;
    render(PLAY_END);
    quiet(true);
  }

  function tick(now) {
    if (!playing) return;
    if (!playStart) playStart = now;
    const t = clamp((now - playStart) / PLAY_MS);
    render(t * PLAY_END);
    if (t >= 1) return finish();
    requestAnimationFrame(tick);
  }

  function play() {
    if (playing || !armed) return;
    playing = true;
    playStart = 0;
    render(0);
    quiet(false);
    requestAnimationFrame(tick);
  }

  /** The button has two jobs and the run's own state picks between them:
   *  while it is playing, end it early and settle on the finished drawing;
   *  when nothing is running, play it again from the first frame.
   *
   *  ⚠️ REPLAY RE-ARMS DELIBERATELY. `armed` is the scroll rule — the band
   *  plays itself once per arrival and does not replay until the reader has
   *  gone a full viewport back above it — and `finish()` clears it. A press is
   *  not an arrival, so it is allowed past that rule: the reader asked for it
   *  in as many words. What it must NOT do is leave the flag set afterwards,
   *  or the next scroll into the band would fire a second run on top of this
   *  one; `play()` is what consumes it and `finish()` clears it again at the
   *  end, so the rule closes behind the replay on its own.
   *
   *  `playStart = 0` is what makes it the FIRST frame rather than a resumed
   *  one: `tick` seeds it from the next animation frame's timestamp. */
  function replay() {
    playing = false;
    playStart = 0;
    armed = true;
    play();
  }

  function onButton() {
    if (playing) finish();
    else replay();
  }

  /* --- wiring ------------------------------------------------------------ */

  /** ⚠️ The observer needs an explicit root above 990px. Horizon scrolls
   *  `.page-wrapper` rather than the document up there, and an
   *  IntersectionObserver with the default viewport root reports against the
   *  wrong scroller — the band would never be seen to arrive on desktop.
   *  @theme/scroll-container's helper is what knows which it is. */
  let seen = null;
  let rearmer = null;

  /** ⚠️ TWO OBSERVERS, because the two questions are different shapes.
   *
   *  PLAY asks "has the band settled?" — a ratio. At 0.8 it is most of the
   *  screen and the reader has plainly arrived rather than caught a glimpse of
   *  it on the way past.
   *
   *  RE-ARM asks "how far back above it has the reader gone?" — a DISTANCE,
   *  and the client's rule is precise: the band does not play again until a
   *  full viewport height has been scrolled back past it. Not "out of view",
   *  which is what this did for an hour and which meant scrolling down past
   *  the band and back up replayed it.
   *
   *  The distance is expressed as a root that extends ONE FULL ROOT HEIGHT
   *  below the fold (`rootMargin` takes percentages, and they resolve against
   *  the root's own box, so this needs no measuring and survives a resize or
   *  a mobile URL bar on its own). Missing THAT root means the band's top is
   *  more than a viewport below the fold.
   *
   *  ⚠️ And the direction test is what makes it the client's rule rather than
   *  half of it: `boundingClientRect.top > 0` means the band is BELOW the
   *  reader. Leaving it out would re-arm the band when the reader scrolls
   *  DOWN away from it too, which is exactly the case that is supposed to
   *  stay disarmed. */
  function watch() {
    unwatch();
    /* ⚠️ `scrollRoot`, not `root`: `root` is this section's own element in
       build()'s scope, and shadowing it here would observe the scroller
       instead of the band. */
    const scrollRoot = getIntersectionRoot();

    seen = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.intersectionRatio >= 0.8) play();
      },
      { root: scrollRoot, threshold: 0.8 }
    );
    seen.observe(root);

    rearmer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting || entry.boundingClientRect.top <= 0) continue;
          armed = true;
          /* Reset to the first frame HERE rather than at the next play, so a
             reader coming back down meets an empty stage filling in, not a
             finished drawing that snaps to empty when it settles. The band is
             a screen below the fold at this moment, so nothing is seen to
             change. */
          render(0);
        }
      },
      { root: scrollRoot, rootMargin: '0px 0px 100% 0px', threshold: 0 }
    );
    rearmer.observe(root);
  }

  function unwatch() {
    seen?.disconnect();
    rearmer?.disconnect();
    seen = null;
    rearmer = null;
  }

  function attach() {
    if (REDUCED?.matches) {
      detach();
      /* The finished scene, held — `tDone` is the last frame of the sequence.
         That, not a blank stage, is the correct resting state. The beats are
         not touched: the stylesheet lays them out as a plain numbered list
         under the drawing whenever motion is off, so all of them are readable
         at once, and the control stays hidden because there is nothing
         running for it to stop. */
      render(P.tDone);
      for (const el of beats) el.removeAttribute('data-active');
      /* Back to the no-JS shape: no box at all, because nothing can ever run
         for it to stop. */
      quiet(false);
      btn?.setAttribute('hidden', '');
      return;
    }
    /* The box exists from here on and never leaves the layout again; only its
       visibility moves. See `quiet()`. */
    btn?.removeAttribute('hidden');
    quiet(true);
    /* Before the run, the drawing is at its first frame: the road has not
       arrived and nothing is painted. */
    render(0);
    watch();
  }

  function detach() {
    unwatch();
    playing = false;
  }

  btn?.addEventListener('click', onButton);
  /* Checked live rather than once at load — the OS setting can change mid-session. */
  REDUCED?.addEventListener('change', attach);
  /* bfcache restores a frozen render; without this the band would come back
     mid-run and never finish, because the rAF chain died with the page. */
  globalThis.addEventListener('pageshow', () => {
    if (!playing) return;
    playing = false;
    finish();
  });

  attach();
  return { detach };
}

/* ⚠️ A top-level querySelectorAll is not enough — editable-sections-standard
   §1 rule 4. Shopify replaces a section's DOM through the Section Rendering API
   when Susanna edits that band, an inserted <script> never executes, and a
   module already in the module map does not re-evaluate. Without this the band
   goes DEAD in the theme editor on her first edit, and nothing in CI catches
   it: the person who hits it is the client, repeatedly, on the one page she
   edits. */
doc.addEventListener('shopify:section:load', (event) => mount(event.target));
doc.addEventListener('shopify:section:unload', (event) => {
  for (const root of event.target.querySelectorAll?.('[data-reitti]') ?? []) {
    mounted.get(root)?.detach();
    mounted.delete(root);
  }
});

mount(doc);
